import React from 'react';
import { FiInfo, FiUsers, FiMail, FiCalendar, FiBarChart2 } from 'react-icons/fi';
import '../../../styles/CampaignWizard.css';

const ReviewCampaign = ({ campaign, templates = [] }) => {
  // Find template names for sequences
  const getTemplateName = (templateId) => {
    const template = templates.find(t => t._id === templateId);
    return template ? template.name : 'Custom Template';
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };
  
  // Format time for display
  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours, 10));
    date.setMinutes(parseInt(minutes, 10));
    
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    });
  };
  
  return (
    <div className="wizard-step-container">
      <div className="wizard-step-header">
        <h2>Review Campaign</h2>
        <p>Review your campaign settings before launching.</p>
      </div>
      
      <div className="review-container">
        <div className="review-section">
          <h3>
            <FiInfo className="icon" />
            Campaign Information
          </h3>
          
          <div className="review-card">
            <div className="review-item">
              <div className="review-label">Name:</div>
              <div className="review-value">{campaign.name}</div>
            </div>
            
            <div className="review-item">
              <div className="review-label">Description:</div>
              <div className="review-value">{campaign.description || 'Not provided'}</div>
            </div>
            
            <div className="review-item">
              <div className="review-label">Type:</div>
              <div className="review-value">
                {campaign.campaignType === 'drip' ? 'Drip Campaign' : 'Regular Campaign'}
              </div>
            </div>
            
            <div className="review-item">
              <div className="review-label">Tags:</div>
              <div className="review-value">{campaign.tags || 'None'}</div>
            </div>
            
            <div className="review-item">
              <div className="review-label">Goal:</div>
              <div className="review-value">
                {campaign.primaryGoal ? campaign.primaryGoal.charAt(0).toUpperCase() + campaign.primaryGoal.slice(1) : 'Not specified'}
                {campaign.primaryGoal === 'other' && campaign.customGoal ? ` - ${campaign.customGoal}` : ''}
              </div>
            </div>
          </div>
        </div>
        
        <div className="review-section">
          <h3>
            <FiUsers className="icon" />
            Leads
          </h3>
          
          <div className="review-card">
            <div className="review-item">
              <div className="review-label">Total Leads:</div>
              <div className="review-value">{campaign.leads.length}</div>
            </div>
            
            {campaign.leads.length > 0 && (
              <>
                <div className="review-item">
                  <div className="review-label">Sample Leads:</div>
                  <div className="review-value">
                    <ul className="sample-leads-list">
                      {campaign.leads.slice(0, 5).map((lead, index) => (
                        <li key={index}>{lead.name || lead.email}</li>
                      ))}
                      {campaign.leads.length > 5 && <li>... and {campaign.leads.length - 5} more</li>}
                    </ul>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        
        <div className="review-section">
          <h3>
            <FiMail className="icon" />
            Email Sequence
          </h3>
          
          <div className="review-card">
            <div className="review-item">
              <div className="review-label">Emails:</div>
              <div className="review-value">{campaign.sequences.length}</div>
            </div>
            
            {campaign.sequences.map((sequence, index) => (
              <div key={index} className="email-preview">
                <div className="email-preview-header">
                  <div className="email-subject">
                    <strong>Email {index + 1}:</strong> {sequence.subject}
                  </div>
                  <div className="email-timing">
                    {index === 0 ? 
                      'Initial Email' : 
                      `Sent ${sequence.delayDays} day${sequence.delayDays !== 1 ? 's' : ''} ${sequence.delayHours > 0 ? `${sequence.delayHours} hour${sequence.delayHours !== 1 ? 's' : ''}` : ''} after previous email`
                    }
                  </div>
                  {sequence.templateId && (
                    <div className="email-template">
                      Template: {getTemplateName(sequence.templateId)}
                    </div>
                  )}
                </div>
                
                <div className="email-preview-body">
                  {sequence.content ? (
                    <div dangerouslySetInnerHTML={{ __html: sequence.content }} />
                  ) : (
                    <em>No content provided</em>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="review-section">
          <h3>
            <FiCalendar className="icon" />
            Schedule
          </h3>
          
          <div className="review-card">
            <div className="review-item">
              <div className="review-label">Start Date:</div>
              <div className="review-value">{formatDate(campaign.schedule.startDate)}</div>
            </div>
            
            <div className="review-item">
              <div className="review-label">End Date:</div>
              <div className="review-value">
                {campaign.schedule.endDate ? formatDate(campaign.schedule.endDate) : 'No end date'}
              </div>
            </div>
            
            <div className="review-item">
              <div className="review-label">Sending Hours:</div>
              <div className="review-value">
                {formatTime(campaign.schedule.sendingHours.from)} - {formatTime(campaign.schedule.sendingHours.to)} 
                ({campaign.schedule.sendingHours.timezone})
              </div>
            </div>
            
            <div className="review-item">
              <div className="review-label">Sending Days:</div>
              <div className="review-value">
                {campaign.schedule.sendingDays.join(', ')}
              </div>
            </div>
            
            {campaign.schedule.dailyLimit && (
              <div className="review-item">
                <div className="review-label">Daily Limit:</div>
                <div className="review-value">
                  {campaign.schedule.dailyLimit} emails per day
                </div>
              </div>
            )}
            
            {campaign.schedule.throttle && (
              <div className="review-item">
                <div className="review-label">Throttle:</div>
                <div className="review-value">
                  {campaign.schedule.throttle} emails per hour
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="review-section">
          <h3>
            <FiBarChart2 className="icon" />
            Campaign Summary
          </h3>
          
          <div className="review-summary">
            <div className="summary-header">Campaign Details</div>
            
            <div className="summary-item">
              <div className="summary-label">Campaign Name:</div>
              <div className="summary-value">{campaign.name}</div>
            </div>
            
            <div className="summary-item">
              <div className="summary-label">Total Recipients:</div>
              <div className="summary-value">{campaign.leads.length}</div>
            </div>
            
            <div className="summary-item">
              <div className="summary-label">Emails in Sequence:</div>
              <div className="summary-value">{campaign.sequences.length}</div>
            </div>
            
            <div className="summary-item">
              <div className="summary-label">Launch Date:</div>
              <div className="summary-value">{formatDate(campaign.schedule.startDate)}</div>
            </div>
            
            <div className="summary-item">
              <div className="summary-label">Estimated Completion:</div>
              <div className="summary-value">
                {campaign.schedule.endDate ? 
                  formatDate(campaign.schedule.endDate) : 
                  'Ongoing'
                }
              </div>
            </div>
            
            {campaign.openRateTarget && (
              <div className="summary-item">
                <div className="summary-label">Open Rate Target:</div>
                <div className="summary-value">{campaign.openRateTarget}%</div>
              </div>
            )}
            
            {campaign.clickRateTarget && (
              <div className="summary-item">
                <div className="summary-label">Click Rate Target:</div>
                <div className="summary-value">{campaign.clickRateTarget}%</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewCampaign;